import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { EarnCustomerRewardsProgram } from './components/EarnCustomerRewardsProgram'
import { EarnDistributionDialog } from './components/EarnDistributionDialog'
import { EarnMyAssets } from './components/EarnMyAssets'
import { EarnMyRewards } from './components/EarnMyRewards'
import { EarnNoPortfolio } from './components/EarnNoPortfolio'
import { EarnNoProducts } from './components/EarnNoProducts'
import { EarnNoWallets } from './components/EarnNoWallets'
import { EarnPageHeader } from './components/EarnPageHeader'
import { EarnSummary } from './components/EarnSummary'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnPageData } from './hooks/useEarnPageData'
import { useEarnTracking } from './hooks/useEarnTracking'
import { EARN_DASHBOARD_STATE, EARN_SHEET_ACTION_TYPE, EarnAsset } from './types'

export const Earn = () => (
  <EarnSheetProvider>
    <EarnContent />
  </EarnSheetProvider>
)

const EarnContent = () => {
  const { t } = useTranslation()
  const { trackPageView } = useEarnTracking()
  const { handleSheetOpen } = useEarnSheetContext()
  const [isDistributionDialogOpen, setIsDistributionDialogOpen] = useState(false)

  const handleDeposit = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
        header: t('earn.depositSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const handleWithdraw = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
        header: t('earn.withdrawSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const earnPageData = useEarnPageData({ onDeposit: handleDeposit, onWithdraw: handleWithdraw })
  const { isStatisticsLoading, isDirectEarning, canManageDistribution } = earnPageData

  useEffect(() => {
    trackPageView('home')
  }, [trackPageView])

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.LOADING) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return <EarnNoProducts />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_WALLETS) {
    return <EarnNoWallets featuredProduct={earnPageData.featuredProduct} />
  }

  if (earnPageData.dashboardState === EARN_DASHBOARD_STATE.NO_PORTFOLIOS) {
    return <EarnNoPortfolio assets={earnPageData.assets} showCustomerEarnRate={!isDirectEarning} />
  }

  const {
    assets,
    epcTakesEarn,
    distributionProductId,
    summary,
    totalEarnings,
    customerRewardsProgram,
  } = earnPageData

  if (isDirectEarning) {
    return (
      <>
        <EarnPageHeader />
        <Box flexCol gap={24}>
          <EarnSummary
            allocatedFunds={summary.allocatedFunds}
            availableFunds={summary.availableFunds}
            totalAllTimeEarnings={summary.totalAllTimeEarnings}
            isStatisticsLoading={isStatisticsLoading}
          />
          <EarnMyAssets assets={assets} showCustomerEarnRate={false} />
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnPageHeader />
      <Box flexCol gap={24}>
        <Box flexCol gap={24} className="items-stretch lg:flex-row">
          <Box className="flex-1 min-w-0">
            <EarnMyRewards
              allocatedFunds={summary.allocatedFunds}
              availableFunds={summary.availableFunds}
              isStatisticsLoading={isStatisticsLoading}
              totalEarnings={totalEarnings.totalEarnings}
              allocationEarnings={totalEarnings.allocationEarnings}
              customerEarnings={totalEarnings.customerEarnings}
            />
          </Box>
          <Box className="flex-1 min-w-0">
            <EarnCustomerRewardsProgram
              onDistributeEarningsClick={() => setIsDistributionDialogOpen(true)}
              epcTakesEarn={epcTakesEarn}
              canManageDistribution={canManageDistribution}
              isStatisticsLoading={isStatisticsLoading}
              {...customerRewardsProgram}
            />
          </Box>
        </Box>
        <EarnMyAssets assets={assets} showCustomerEarnRate={!isDirectEarning} />
        {distributionProductId && (
          <EarnDistributionDialog
            open={isDistributionDialogOpen}
            onClose={() => setIsDistributionDialogOpen(false)}
            productId={distributionProductId}
          />
        )}
      </Box>
    </>
  )
}
